import { Component, OnInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { OAuthService } from 'angular-oauth2-oidc';
import { catchError, finalize, of, timeout } from 'rxjs';
import { DataService } from '../data.service';

/**
 *  CSALÁDI BELÉPÉSEK.
 *
 *  A törzsadat és a belépési rendszer külön él: attól, hogy a másik szülő szerepel a
 *  nyilvántartásban, még nincs fiókja – és eddig emiatt hónapokig nem látta a gyereke
 *  jegyeit, mert az iskolát kellett megkeresni a pótlásért.
 *
 *  Itt a család maga pótolhatja. A kört a KISZOLGÁLÓ állítja össze a belépett
 *  felhasználó törzsadatából, ezért ezen a képernyőn nincs keresés és nincs
 *  azonosító-beírás: csak azok jelennek meg, akikhez a belépőnek köze van.
 */
@Component({
  selector: 'app-csaladi-belepes',
  templateUrl: './csaladi-belepes.component.html',
  styleUrls: ['./csaladi-belepes.component.scss'],
})
export class CsaladiBelepesComponent implements OnInit {
  user$: any;
  sajatIntezmeny: any;
  kilet: any = null;
  tagok: any[] = [];
  toltodik = false;
  hiba = '';
  keycloak = true;
  uzenet = '';

  // Az imént létrehozott belépés – a jelszót EGYSZER mutatjuk meg, utána már sehol
  // nem kérdezhető vissza (nem is tároljuk).
  ujBelepes: any = null;
  letrehozasFolyamatban = '';

  /*  ÚJ GONDVISELŐ FELVÉTELE. Ha a másik szülő nemcsak fiók, hanem a nyilvántartásból
   *  is hiányzik, itt vehető fel: a törzsadat, a gyerekhez kötés és a belépés együtt
   *  készül el – fiók önmagában annyit érne, hogy be tud lépni, de a gyerekét nem látná. */
  ujForm = false;
  ujGondviselo: any = { vez: '', ker: '', email: '', szul_ido: '', tanulo: '', rokonsag: 1 };
  rokonsagok = [
    { id: 1, nev: 'Szülő / Gondviselő' },
    { id: 7, nev: 'Gyám' },
    { id: 6, nev: 'Nagyszülő' },
    { id: 8, nev: 'Egyéb rokoni kapcsolat' },
  ];

  constructor(private http: HttpClient, private oauth: OAuthService, private data: DataService) {}

  async ngOnInit() {
    await this.oauth.loadUserProfile();
    this.user$ = this.data.belepoKilet(this.oauth.getIdentityClaims());
    this.sajatIntezmeny = await this.data.getFromStorage('sajatIntezmeny');
    this.betoltes();
  }

  get intezmenyId(): string {
    return String(this.sajatIntezmeny?.intezmenyId || this.sajatIntezmeny?.id || '').trim();
  }

  get hianyzik(): number {
    return this.tagok.filter((t) => !t.van).length;
  }

  /*  Kihez lehet gondviselőt felvenni: a szülő a gyerekeihez, a tanuló önmagához. */
  get gyerekek(): any[] {
    if (this.kilet?.fajta === 'tanulo') {
      return [{ azonosito: this.kilet.azonosito, nev: this.kilet.nev + ' (te)' }];
    }
    return this.tagok.filter((t) => t.fajta === 'tanulo');
  }

  betoltes() {
    if (!this.user$?.username || !this.intezmenyId) return;
    this.toltodik = true;
    this.hiba = '';
    this.http
      .get<any>(`/api/csalad-belepes/${encodeURIComponent(this.user$.username)}/${this.intezmenyId}`)
      .pipe(
        timeout(30_000),
        catchError((err) => {
          this.hiba = err?.error?.error || 'A családi belépések most nem érhetők el.';
          return of(null);
        }),
        finalize(() => { this.toltodik = false; }),
      )
      .subscribe((valasz: any) => {
        if (!valasz) return;
        this.kilet = valasz.kilet || null;
        this.tagok = (Array.isArray(valasz.tagok) ? valasz.tagok : [])
          // A cím a létrehozás ELŐTT javítható; a szerkesztett érték külön mezőben él,
          // hogy a lista frissítése ne írja felül, amit a felhasználó épp beírt.
          .map((t: any) => ({ ...t, ujEmail: t.email || '' }));
        if (!this.ujGondviselo.tanulo && this.gyerekek.length) {
          this.ujGondviselo.tanulo = this.gyerekek[0].azonosito;
        }
        this.keycloak = valasz.keycloak !== false;
        this.uzenet = valasz.uzenet || '';
      });
  }

  /**
   *  Belépés létrehozása egy családtagnak.
   *
   *  Megerősítést kérünk, mert ez a belépési rendszerbe ír: a fiók létrejötte után a
   *  jelszó már csak visszaállítással cserélhető, nem „vonható vissza" a művelet.
   */
  letrehoz(tag: any) {
    if (!tag?.azonosito || this.letrehozasFolyamatban) return;
    if (!confirm(`Létrehozzuk a belépést neki: ${tag.nev}?\n\nA jelszót egyszer mutatjuk meg, `
      + 'és az első belépéskor a rendszer jelszócserét kér.')) return;

    this.letrehozasFolyamatban = tag.azonosito;
    this.ujBelepes = null;
    this.http
      .post<any>(`/api/csalad-belepes/${encodeURIComponent(this.user$.username)}/${this.intezmenyId}`,
        { azonosito: tag.azonosito, email: String(tag.ujEmail || '').trim() })
      .pipe(
        timeout(30_000),
        catchError((err) => {
          this.data.sendToast(err?.error?.error || 'A belépést nem sikerült létrehozni.', 'error');
          // Ha azért nem sikerült, mert időközben létrejött, a lista frissítése mutatja meg.
          if (err?.error?.van) this.betoltes();
          return of(null);
        }),
        finalize(() => { this.letrehozasFolyamatban = ''; }),
      )
      .subscribe((valasz: any) => {
        if (!valasz?.ok) return;
        this.ujBelepes = valasz;
        this.data.sendToast('A belépés elkészült.');
        this.betoltes();
      });
  }

  /**
   *  Új gondviselő felvétele: törzsadat + gyerekhez kötés + belépés, egy lépésben.
   *
   *  Ha a megadott e-mail címhez már tartozik fiók a belépési rendszerben, de
   *  gondviselői rekord nem, akkor a kiszolgáló azt a fiókot veszi át – ilyenkor a
   *  felhasználónév a MEGLÉVŐ fióké lesz, és a képernyő ezt ki is írja.
   */
  ujGondviseloMentes() {
    const u = this.ujGondviselo;
    if (!String(u.vez || '').trim() || !String(u.ker || '').trim()) {
      this.data.sendToast('A vezetéknév és a keresztnév kötelező.', 'warning');
      return;
    }
    if (!u.tanulo) { this.data.sendToast('Válaszd ki, melyik gyermekhez tartozik.', 'warning'); return; }
    if (!confirm(`Felvesszük gondviselőnek: ${u.vez} ${u.ker}, és belépést is készítünk neki?`)) return;

    this.letrehozasFolyamatban = 'uj';
    this.ujBelepes = null;
    this.http
      .post<any>(`/api/csalad-belepes/${encodeURIComponent(this.user$.username)}/${this.intezmenyId}`,
        { uj: true, vez: String(u.vez).trim(), ker: String(u.ker).trim(),
          email: String(u.email || '').trim(), szul_ido: u.szul_ido || '',
          tanulo: u.tanulo, rokonsag: u.rokonsag })
      .pipe(
        timeout(30_000),
        catchError((err) => {
          this.data.sendToast(err?.error?.error || 'A gondviselőt nem sikerült felvenni.', 'error');
          return of(null);
        }),
        finalize(() => { this.letrehozasFolyamatban = ''; }),
      )
      .subscribe((valasz: any) => {
        if (!valasz?.ok) return;
        this.ujBelepes = valasz;
        this.ujForm = false;
        this.ujGondviselo = { vez: '', ker: '', email: '', szul_ido: '', tanulo: '', rokonsag: 1 };
        this.data.sendToast('A gondviselő és a belépése elkészült.');
        this.betoltes();
      });
  }

  masol() {
    if (!this.ujBelepes) return;
    const szoveg = `Felhasználónév: ${this.ujBelepes.azonosito}\nJelszó: ${this.ujBelepes.jelszo}`;
    navigator.clipboard?.writeText(szoveg)
      .then(() => this.data.sendToast('A belépési adatok a vágólapon.'))
      .catch(() => this.data.sendToast('A másolás nem sikerült – írd ki kézzel.', 'warning'));
  }

  bezarUj() { this.ujBelepes = null; }
}
